import { useEffect, useMemo, useState } from 'react'
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  LinkIcon,
  ServerStackIcon,
} from '@heroicons/react/24/outline'
import {
  Badge,
  Button,
  Input,
  Panel,
  PanelBody,
  PanelDescription,
  PanelHeader,
  PanelTitle,
  Select,
  SelectOption,
} from '../../components/ui'
import { cn } from '../../shared/lib/cn'
import {
  useClearVariableLagDataSource,
  useSetVariableLagDataSource,
  useTestVariableLagConnection,
  useVariableLagAvailableConnectors,
  useVariableLagDataSourceConfig,
  useVariableLagHealth,
} from './api'
import type { VariableLagConfig } from './types'

const DEFAULT_SCHEMA = 'variable_lag_accruals'

export default function ConfigPage() {
  const { data: config, isLoading: configLoading } = useVariableLagDataSourceConfig()
  const { data: health, isLoading: healthLoading } = useVariableLagHealth()
  const { data: connectors = [], isLoading: connectorsLoading } = useVariableLagAvailableConnectors()
  const setDataSource = useSetVariableLagDataSource()
  const clearDataSource = useClearVariableLagDataSource()
  const testConnection = useTestVariableLagConnection()

  const [connectorId, setConnectorId] = useState('')
  const [schema, setSchema] = useState(DEFAULT_SCHEMA)
  const [bootstrapped, setBootstrapped] = useState(false)

  useEffect(() => {
    if (!config || bootstrapped) return
    setConnectorId(config.connector_id ?? '')
    setSchema(config.redshift_schema || DEFAULT_SCHEMA)
    setBootstrapped(true)
  }, [bootstrapped, config])

  const activeConnector = useMemo(
    () => connectors.find(connector => connector.connector_id === connectorId),
    [connectorId, connectors],
  )

  const isLoading = configLoading || healthLoading || connectorsLoading
  const trimmedSchema = schema.trim() || DEFAULT_SCHEMA
  const canSubmit = Boolean(connectorId && trimmedSchema)

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-gray-500 dark:text-gray-400">
        Loading configuration...
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-[1180px] space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-charcoal dark:text-gray-100">
            Configuration
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Bind Variable Lag Accruals to the Redshift schema used for close evidence.
          </p>
        </div>
        <ConnectionBadges config={config} health={health} />
      </div>

      <Panel>
        <PanelHeader>
          <PanelTitle>Data Source</PanelTitle>
          <PanelDescription>
            Select the saved Redshift connector and table schema for this applet.
          </PanelDescription>
        </PanelHeader>
        <PanelBody className="space-y-5">
          <div className="grid grid-cols-[minmax(0,1.15fr)_minmax(260px,0.85fr)] gap-4 max-lg:grid-cols-1">
            <div className="space-y-2">
              <label className="text-sm font-medium text-charcoal dark:text-gray-100">
                Redshift Connector
              </label>
              <Select
                value={connectorId}
                onValueChange={setConnectorId}
                placeholder={connectors.length ? 'Select a connector' : 'No Redshift connectors available'}
                disabled={!connectors.length}
                icon={<LinkIcon />}
              >
                {connectors.map(connector => (
                  <SelectOption key={connector.connector_id} value={connector.connector_id}>
                    {connector.connector_name}
                  </SelectOption>
                ))}
              </Select>
              {config?.connector_name && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Current: {config.connector_name}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-charcoal dark:text-gray-100">
                Redshift Schema
              </label>
              <Input
                value={schema}
                onChange={event => setSchema(event.target.value)}
                placeholder={DEFAULT_SCHEMA}
              />
              {config?.redshift_schema && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Current: {config.redshift_schema}
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="brand"
              onClick={() =>
                setDataSource.mutate({
                  connector_id: connectorId,
                  redshift_schema: trimmedSchema,
                })
              }
              disabled={!canSubmit || setDataSource.isPending}
            >
              {setDataSource.isPending ? <ArrowPathIcon className="animate-spin" /> : <CheckCircleIcon />}
              {setDataSource.isPending ? 'Saving' : 'Save Configuration'}
            </Button>
            <Button
              variant="outline"
              onClick={() =>
                testConnection.mutate({
                  connector_id: connectorId,
                  redshift_schema: trimmedSchema,
                })
              }
              disabled={!canSubmit || testConnection.isPending}
            >
              {testConnection.isPending ? <ArrowPathIcon className="animate-spin" /> : <ServerStackIcon />}
              {testConnection.isPending ? 'Testing' : 'Test Connection'}
            </Button>
            {config?.mode === 'saved' && (
              <Button
                variant="destructive"
                onClick={() => {
                  if (window.confirm('Clear the saved Variable Lag Accruals Redshift connector?')) {
                    clearDataSource.mutate()
                  }
                }}
                disabled={clearDataSource.isPending}
              >
                {clearDataSource.isPending ? 'Clearing' : 'Clear Saved Connector'}
              </Button>
            )}
          </div>

          <MutationStatus
            saved={setDataSource.isSuccess}
            saveError={setDataSource.error}
            cleared={clearDataSource.isSuccess}
            clearError={clearDataSource.error}
          />
          <ConnectionTestResult
            result={testConnection.data}
            error={testConnection.error}
          />
        </PanelBody>
      </Panel>

      <Panel>
        <PanelHeader>
          <PanelTitle>Active Connection</PanelTitle>
        </PanelHeader>
        <PanelBody>
          <dl className="grid grid-cols-2 gap-4 text-sm lg:grid-cols-5">
            <ConnectionField label="Connector" value={config?.connector_name || activeConnector?.connector_name || '-'} />
            <ConnectionField label="Mode" value={modeLabel(config?.mode)} />
            <ConnectionField label="Schema" value={config?.redshift_schema || trimmedSchema} monospace />
            <ConnectionField label="Tables" value="variable_lag_*" monospace />
            <ConnectionField label="Host" value={config?.host || '-'} monospace />
          </dl>
        </PanelBody>
      </Panel>

      <Panel>
        <PanelHeader>
          <PanelTitle>Redshift Table Contract</PanelTitle>
        </PanelHeader>
        <PanelBody>
          <div className="grid grid-cols-2 gap-2 text-sm max-md:grid-cols-1">
            {[
              'variable_lag_runs',
              'variable_lag_source_files',
              'variable_lag_categories',
              'variable_lag_te_items',
              'variable_lag_legal_items',
              'variable_lag_event_items',
              'variable_lag_marketing_items',
              'variable_lag_card_evidence',
              'variable_lag_exports',
              'variable_lag_next_run_averages',
              'variable_lag_reports',
            ].map(table => (
              <div
                key={table}
                className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-xs text-charcoal dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-200"
              >
                {table}
              </div>
            ))}
          </div>
        </PanelBody>
      </Panel>
    </div>
  )
}

function ConnectionBadges({
  config,
  health,
}: {
  config?: VariableLagConfig
  health?: { redshift_connected: boolean; langfuse_enabled: boolean; mode?: string }
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Badge variant={health?.redshift_connected ? 'success' : 'danger'}>
        Redshift {health?.redshift_connected ? 'connected' : 'not connected'}
      </Badge>
      <Badge variant="secondary">{modeLabel(config?.mode || health?.mode)}</Badge>
      <Badge variant={health?.langfuse_enabled ? 'success' : 'warning'}>
        Langfuse {health?.langfuse_enabled ? 'enabled' : 'inactive'}
      </Badge>
    </div>
  )
}

function ConnectionField({
  label,
  value,
  monospace = false,
}: {
  label: string
  value: string
  monospace?: boolean
}) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {label}
      </dt>
      <dd
        className={cn(
          'mt-1 truncate font-medium text-charcoal dark:text-gray-100',
          monospace && 'font-mono text-xs',
        )}
        title={value}
      >
        {value}
      </dd>
    </div>
  )
}

function MutationStatus({
  saved,
  saveError,
  cleared,
  clearError,
}: {
  saved: boolean
  saveError: Error | null
  cleared: boolean
  clearError: Error | null
}) {
  if (!saved && !saveError && !cleared && !clearError) return null
  const isError = Boolean(saveError || clearError)
  return (
    <div
      className={cn(
        'flex items-start gap-2 rounded-md border px-3 py-2 text-sm',
        isError
          ? 'border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-900/60 dark:bg-rose-950/20 dark:text-rose-200'
          : 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900/60 dark:bg-emerald-950/20 dark:text-emerald-200',
      )}
    >
      {isError ? <ExclamationTriangleIcon className="mt-0.5 h-4 w-4" /> : <CheckCircleIcon className="mt-0.5 h-4 w-4" />}
      <span>{saveError?.message || clearError?.message || (cleared ? 'Saved connector cleared.' : 'Configuration saved.')}</span>
    </div>
  )
}

function ConnectionTestResult({
  result,
  error,
}: {
  result?: { redshift_connected: boolean; message: string }
  error: Error | null
}) {
  if (!result && !error) return null
  const connected = Boolean(result?.redshift_connected)
  return (
    <div
      className={cn(
        'flex items-start gap-2 rounded-md border px-3 py-2 text-sm',
        connected
          ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900/60 dark:bg-emerald-950/20 dark:text-emerald-200'
          : 'border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-900/60 dark:bg-rose-950/20 dark:text-rose-200',
      )}
    >
      {connected ? <CheckCircleIcon className="mt-0.5 h-4 w-4" /> : <ExclamationTriangleIcon className="mt-0.5 h-4 w-4" />}
      <span>{error?.message || result?.message || 'Connection test failed.'}</span>
    </div>
  )
}

function modeLabel(mode?: string | null): string {
  if (mode === 'saved') return 'Saved connector'
  if (mode === 'env_fallback') return 'Env fallback'
  if (mode === 'unconfigured') return 'Unconfigured'
  return mode || 'Unknown'
}
